import React, { useState } from 'react';
import RichText from './RichText';
import { Hoja, parseHojas, serializeHojas, limpiarNombre, nombreNuevaHoja, hojaVacia } from './hojas';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onBlur?: (v: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: number | string;
  /* Índice de la hoja abierta. Se puede controlar desde afuera para que el
     recuadro chico y el modal de "Expandir" muestren la misma hoja. */
  activa?: number;
  onActiva?: (i: number) => void;
  /* Hoja blanca, ver RichText. La pestaña activa se funde con la hoja. */
  papel?: boolean;
}

const TAB: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '4px 10px', borderRadius: '6px 6px 0 0',
  border: '1px solid transparent', borderBottom: 'none',
  background: 'none', color: 'var(--text-muted)',
  fontSize: 12, lineHeight: '18px', cursor: 'pointer', whiteSpace: 'nowrap',
};

const TAB_ACTIVA: React.CSSProperties = {
  ...TAB,
  background: 'rgba(212,175,55,0.12)',
  border: '1px solid rgba(212,175,55,0.4)',
  borderBottom: 'none',
  color: 'var(--white)',
  fontWeight: 600,
};

const TAB_PAPEL: React.CSSProperties = {
  ...TAB_ACTIVA,
  background: '#FFFFFF',
  border: '1px solid #FFFFFF',
  borderBottom: 'none',
  color: '#1C2B45',
};

const HojasEditor: React.FC<Props> = ({
  value, onChange, onBlur, readOnly, placeholder, minHeight = 120, activa, onActiva, papel,
}) => {
  const activaStyle = papel ? TAB_PAPEL : TAB_ACTIVA;
  const [activaLocal, setActivaLocal] = useState(0);
  const [editando, setEditando] = useState<number | null>(null);
  const [nombreTmp, setNombreTmp] = useState('');

  const hojas = parseHojas(value);
  const idx = Math.min(Math.max(activa ?? activaLocal, 0), hojas.length - 1);
  const irA = (i: number) => { setEditando(null); if (onActiva) onActiva(i); else setActivaLocal(i); };

  const guardar = (hs: Hoja[], destino: number, cb: (v: string) => void) => {
    cb(serializeHojas(hs));
    if (destino !== idx) irA(destino);
  };

  const escribir = (html: string, avisar?: (v: string) => void) => {
    if (!avisar) return;
    avisar(serializeHojas(hojas.map((h, i) => (i === idx ? { ...h, html } : h))));
  };

  const agregar = () => {
    const hs = [...hojas, { nombre: nombreNuevaHoja(hojas), html: '' }];
    guardar(hs, hs.length - 1, v => { onChange(v); onBlur?.(v); });
  };

  const eliminar = (i: number) => {
    if (hojas.length < 2) return;
    if (!hojaVacia(hojas[i]) && !window.confirm(`¿Eliminar la hoja "${hojas[i].nombre}" y todo lo escrito en ella?`)) return;
    const hs = hojas.filter((_, j) => j !== i);
    guardar(hs, Math.max(0, i - 1), v => { onChange(v); onBlur?.(v); });
  };

  const renombrar = (i: number) => {
    const nombre = limpiarNombre(nombreTmp) || hojas[i].nombre;
    setEditando(null);
    if (nombre === hojas[i].nombre) return;
    const hs = hojas.map((h, j) => (j === i ? { ...h, nombre } : h));
    guardar(hs, i, v => { onChange(v); onBlur?.(v); });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, overflowX: 'auto', paddingBottom: 0, paddingLeft: papel ? 6 : 0, borderBottom: papel ? 'none' : '1px solid rgba(255,255,255,0.12)' }}>
        {hojas.map((h, i) => (
          editando === i ? (
            <input
              key={i}
              autoFocus
              value={nombreTmp}
              onChange={e => setNombreTmp(e.target.value)}
              onBlur={() => renombrar(i)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); renombrar(i); }
                // Esc cancela el renombrado y se marca como manejado: la
                // página completa también escucha Esc para cerrarse.
                if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setEditando(null); }
              }}
              style={{ ...activaStyle, width: 120, outline: 'none', padding: '4px 8px' }}
            />
          ) : (
            <button
              key={i}
              type="button"
              onClick={() => irA(i)}
              onDoubleClick={() => { if (!readOnly) { setNombreTmp(h.nombre); setEditando(i); } }}
              title={readOnly ? h.nombre : `${h.nombre} — doble clic para renombrar`}
              style={i === idx ? activaStyle : TAB}
            >
              <span style={{ opacity: hojaVacia(h) && i !== idx ? 0.55 : 1 }}>{h.nombre}</span>
              {!readOnly && i === idx && hojas.length > 1 && (
                <span
                  role="button"
                  title="Eliminar esta hoja"
                  onClick={e => { e.stopPropagation(); eliminar(i); }}
                  style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1, padding: '0 2px' }}
                >✕</span>
              )}
            </button>
          )
        ))}
        {!readOnly && (
          <button type="button" onClick={agregar} title="Agregar hoja" style={{ ...TAB, color: 'var(--text-muted)', fontSize: 15, padding: '4px 9px' }}>+</button>
        )}
      </div>
      {/* key: al cambiar de hoja el editor se rehace, así no queda texto de la anterior */}
      <div style={{ marginTop: papel ? 0 : 6 }}>
        <RichText
          papel={papel}
          key={idx}
          value={hojas[idx].html}
          onChange={html => escribir(html, onChange)}
          onBlur={onBlur ? html => escribir(html, onBlur) : undefined}
          readOnly={readOnly}
          placeholder={placeholder}
          minHeight={minHeight}
        />
      </div>
    </div>
  );
};

export default HojasEditor;
